import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminHeaders } from '../../lib/adminAuth';
import './MessagesPage.css';

import { tx } from '../../lib/adminI18n';
// ------------------------------------------------------------
// 型
// ------------------------------------------------------------
interface Thread {
  guest_email: string;
  guest_name: string | null;
  reservation_count: number;
  message_count: number;
  latest_checkin: string | null;
  last_subject: string | null;
  last_body: string | null;
  last_direction: 'in' | 'out' | null;
  last_kind: string | null;
  last_status: string | null;
  last_message_at: string | null;
  sort_at: string | null;
  property_ids: string[];
  target_reservation_id: string | null;
  target_status: string | null;
  property_id: string | null;
  property_name: string | null;
  checkin_date: string | null;
  checkout_date: string | null;
  passport_sent: boolean;
  non_cancellable: boolean;
}

interface PropertyOption { id: string; name: string; }

type SortKey = 'recent' | 'checkin_asc' | 'checkin_desc';

/** 物件名の略字: 英字は各単語の頭文字（最大3文字）、それ以外は先頭2文字 */
function propertyInitials(name: string | null): string {
  if (!name) return '—';
  const words = name.trim().split(/\s+/).filter((w) => /^[A-Za-z]/.test(w));
  if (words.length > 0) return words.map((w) => w[0].toUpperCase()).join('').slice(0, 3);
  return name.trim().slice(0, 2);
}

/** 物件ごとに丸の色を固定 */
const AVATAR_COLORS = ['#2563eb', '#db2777', '#059669', '#d97706', '#7c3aed', '#0891b2'];
function propertyColor(id: string | null, all: PropertyOption[]): string {
  const i = all.findIndex((p) => p.id === id);
  return i >= 0 ? AVATAR_COLORS[i % AVATAR_COLORS.length] : '#9ca3af';
}

interface Message {
  id: string;
  guest_email: string;
  guest_name: string | null;
  reservation_id: string | null;
  direction: 'in' | 'out';
  kind: 'manual' | 'system' | 'inbound';
  subject: string | null;
  body: string;
  status: string;
  error: string | null;
  created_at: string;
  flagged?: number;
  from_address?: string | null;
}

interface ThreadReservation {
  id: string;
  passport_received?: number;
  checkin_date: string;
  checkout_date: string;
  status: string;
  property_name: string;
}

interface ThreadDetail {
  guest_email: string;
  guest_name: string | null;
  reservations: ThreadReservation[];
  messages: Message[];
}

// D1 の CURRENT_TIMESTAMP は UTC（"YYYY-MM-DD HH:MM:SS"）→ 日本時間で表示
function fmtTime(s: string | null): string {
  if (!s) return '';
  const d = new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

const STATUS_LABEL: Record<string, string> = {
  get PENDING_PAYMENT() { return tx('msg.0'); },
  get PENDING_TRANSFER() { return tx('msg.1'); },
  get CONFIRMED() { return tx('msg.2'); },
  get CANCELLED() { return tx('msg.3'); },
  get EXPIRED() { return tx('msg.4'); },
  get REFUNDED() { return tx('msg.5'); },
  get PAID_EXPIRED() { return tx('msg.6'); },
};

const SEND_STATUS_LABEL: Record<string, string> = {
  get sent() { return tx('msg.7'); },
  get skipped() { return tx('msg.8'); },
  get failed() { return tx('msg.9'); },
  get received() { return tx('msg.10'); },
};

async function readError(res: Response): Promise<string> {
  const e = await res.json().catch(() => ({} as any));
  return [e?.error, e?.detail].filter(Boolean).join(': ') || tx('msg.11', { p0: res.status });
}

// ============================================================
// 一覧画面  /admin/messages
// ============================================================
function ThreadList() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('recent');
  const [menuFor, setMenuFor] = useState<string | null>(null);

  // メニュー外クリック / Esc で閉じる
  useEffect(() => {
    if (!menuFor) return;
    const close = () => setMenuFor(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', onKey); };
  }, [menuFor]);

  const removeThread = async (t: Thread) => {
    setMenuFor(null);
    const name = t.guest_name || t.guest_email;
    if (!window.confirm(tx('msg.12', { p0: name, p1: t.message_count }))) return;
    try {
      const res = await fetch(`/api/admin/messages/threads/${encodeURIComponent(t.guest_email)}`, {
        method: 'DELETE',
        headers: adminHeaders(),
      });
      if (!res.ok) throw new Error(await readError(res));
      await load();
    } catch (e: any) {
      setError(e?.message || tx('msg.13'));
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, pres] = await Promise.all([
        fetch('/api/admin/messages/threads', { headers: adminHeaders() }),
        fetch('/api/admin/properties', { headers: adminHeaders() }),
      ]);
      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      setThreads(data.threads || []);
      if (pres.ok) {
        const pdata = await pres.json();
        const list = Array.isArray(pdata) ? pdata : pdata.properties || [];
        setProperties(list.map((p: any) => ({ id: p.id, name: p.name })));
      }
    } catch (e: any) {
      setError(e instanceof TypeError ? tx('msg.14') : e?.message || tx('msg.15'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const k = q.trim().toLowerCase();
    let list = threads.filter((t) =>
      (!k || t.guest_email.includes(k) || (t.guest_name || '').toLowerCase().includes(k)) &&
      (!propertyFilter || (t.property_ids || []).includes(propertyFilter))
    );
    if (sortKey !== 'recent') {
      const dir = sortKey === 'checkin_asc' ? 1 : -1;
      // チェックイン日がないゲストは常に最後
      list = [...list].sort((a, b) => {
        if (!a.checkin_date && !b.checkin_date) return 0;
        if (!a.checkin_date) return 1;
        if (!b.checkin_date) return -1;
        return a.checkin_date.localeCompare(b.checkin_date) * dir;
      });
    }
    return list;
  }, [threads, q, propertyFilter, sortKey]);

  const toggleCheckinSort = () =>
    setSortKey((s) => (s === 'checkin_asc' ? 'checkin_desc' : 'checkin_asc'));

  return (
    <div className="messages-page">
      <div className="messages-header">
        <h1>{tx('msg.16')}</h1>
        <button type="button" className="btn-reload" onClick={load}>{tx('msg.17')}</button>
      </div>

      <div className="messages-toolbar">
        <input
          className="messages-search"
          type="search"
          placeholder={tx('msg.18')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select value={propertyFilter} onChange={(e) => setPropertyFilter(e.target.value)} title={tx('msg.19')}>
          <option value="">{tx('msg.20')}</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)} title={tx('msg.21')}>
          <option value="recent">{tx('msg.22')}</option>
          <option value="checkin_asc">{tx('msg.23')}</option>
          <option value="checkin_desc">{tx('msg.24')}</option>
        </select>
        <span className="thread-count">{filtered.length} {tx('msg.25')}</span>
      </div>

      {error && <div className="msg-error" role="alert">⚠ {error}</div>}

      {loading ? (
        <p className="msg-empty">{tx('msg.26')}</p>
      ) : filtered.length === 0 ? (
        <p className="msg-empty">{tx('msg.27')}</p>
      ) : (
        <div className="thread-table">
          <div className="thread-table-head">
            <span className="col-avatar" />
            <span className="col-main">{tx('msg.28')}</span>
            <button type="button" className="col-checkin sortable" onClick={toggleCheckinSort}>
              {tx('msg.29')}
              {sortKey === 'checkin_asc' ? ' ▲' : sortKey === 'checkin_desc' ? ' ▼' : ' ⇅'}
            </button>
            <span className="col-state">{tx('msg.30')}</span>
            <span className="col-time">{tx('msg.31')}</span>
            <span className="col-menu" />
          </div>
          <ul className="thread-list">
            {filtered.map((t) => (
              <li
                key={t.guest_email}
                className="thread-row"
                onClick={() => navigate(`/admin/messages/${encodeURIComponent(t.guest_email)}`)}
              >
                <div
                  className="thread-avatar col-avatar"
                  style={{ background: propertyColor(t.property_id, properties) }}
                  title={t.property_name || tx('msg.32')}
                >
                  {propertyInitials(t.property_name)}
                </div>
                <div className="thread-main col-main">
                  <div className="thread-top">
                    <span className="thread-name">{t.guest_name || tx('msg.33')}</span>
                    <span className="thread-email">{t.guest_email}</span>
                  </div>
                  <div className="thread-preview">
                    {t.last_body ? (
                      <>
                        <span className={`dir-badge ${t.last_direction}`}>
                          {t.last_direction === 'in' ? tx('msg.10') : t.last_kind === 'system' ? tx('msg.34') : tx('msg.35')}
                        </span>
                        {t.last_subject && <strong>{t.last_subject}</strong>}
                        <span className="preview-text">{t.last_body.replace(/\s+/g, ' ').slice(0, 80)}</span>
                      </>
                    ) : (
                      <span className="preview-none">{tx('msg.36')}</span>
                    )}
                  </div>
                  <div className="thread-meta">
                    {t.property_name || tx('msg.32')}
                    {t.target_status && tx('msg.37', { p0: STATUS_LABEL[t.target_status] || t.target_status })}
                    {t.reservation_count > 1 && tx('msg.38', { p0: t.reservation_count })}
                  </div>
                </div>
                <div className="col-checkin">
                  {t.checkin_date ? (
                    <>
                      <div>{t.checkin_date}</div>
                      <div className="checkout-sub">〜{t.checkout_date}</div>
                    </>
                  ) : '—'}
                </div>
                <div className="col-state">
                  {t.passport_sent && <span className="state-badge passport">{tx('msg.40')}</span>}
                  {t.non_cancellable && <span className="state-badge noncancel">{tx('msg.41')}</span>}
                  {!t.passport_sent && !t.non_cancellable && <span className="state-none">—</span>}
                </div>
                <div className="thread-time col-time">{fmtTime(t.last_message_at || t.sort_at)}</div>
                <div className="col-menu" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="row-menu-btn"
                    aria-label={tx('msg.42')}
                    aria-haspopup="menu"
                    aria-expanded={menuFor === t.guest_email}
                    onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === t.guest_email ? null : t.guest_email); }}
                  >
                    ⋯
                  </button>
                  {menuFor === t.guest_email && (
                    <div className="row-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        role="menuitem"
                        className="row-menu-item danger"
                        disabled={t.message_count === 0}
                        title={t.message_count === 0 ? tx('msg.43') : undefined}
                        onClick={() => removeThread(t)}
                      >
                        {tx('msg.44')}
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================
// やりとり画面  /admin/messages/:email
// ============================================================
function ThreadView({ email }: { email: string }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ThreadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [reservationId, setReservationId] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mode, setMode] = useState<'out' | 'in'>('out');

  const bottomRef = useRef<HTMLDivElement>(null);
  const [msgMenuFor, setMsgMenuFor] = useState<string | null>(null);

  // メニュー外クリック / Esc で閉じる
  useEffect(() => {
    if (!msgMenuFor) return;
    const close = () => setMsgMenuFor(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', onKey); };
  }, [msgMenuFor]);

  const removeMessage = async (m: Message) => {
    setMsgMenuFor(null);
    const label = m.subject ? tx('msg.45', { p0: m.subject }) : tx('msg.46');
    if (!window.confirm(tx('msg.47', { p0: label }))) return;
    setSendError(null);
    try {
      const res = await fetch(`/api/admin/messages/${encodeURIComponent(m.id)}`, {
        method: 'DELETE',
        headers: adminHeaders(),
      });
      if (!res.ok) throw new Error(await readError(res));
      setNotice(tx('msg.48'));
      await load();
    } catch (e: any) {
      setSendError(e?.message || tx('msg.13'));
    }
  };

  const load = async () => {
    setError(null);
    try {
      const res = await fetch(`/api/admin/messages/threads/${encodeURIComponent(email)}`, {
        headers: adminHeaders(),
      });
      if (!res.ok) throw new Error(await readError(res));
      const data: ThreadDetail = await res.json();
      setDetail(data);
      // 件名の初期値: 直近の件名に「Re:」を付ける
      if (!subject) {
        const lastSubj = [...data.messages].reverse().find((m) => m.subject)?.subject;
        setSubject(lastSubj ? (lastSubj.startsWith('Re:') ? lastSubj : `Re: ${lastSubj}`) : '');
      }
      if (!reservationId && data.reservations[0]) setReservationId(data.reservations[0].id);
    } catch (e: any) {
      setError(e instanceof TypeError ? tx('msg.14') : e?.message || tx('msg.15'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [email]);

  // 新しいメッセージが来たら一番下へスクロール
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages.length]);

  const send = async () => {
    setSendError(null);
    setNotice(null);
    if (mode === 'out' && !subject.trim()) { setSendError(tx('msg.49')); return; }
    if (!body.trim()) { setSendError(tx('msg.50')); return; }
    if (mode === 'out' && !window.confirm(tx('msg.51', { p0: email }))) return;

    setSending(true);
    try {
      const url = `/api/admin/messages/threads/${encodeURIComponent(email)}` + (mode === 'in' ? '/inbound' : '');
      const res = await fetch(url, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ subject: subject.trim(), body: body.trim(), reservation_id: reservationId || undefined }),
      });
      if (!res.ok) throw new Error(await readError(res));
      const data = await res.json();
      setBody('');
      if (mode === 'in') setNotice(tx('msg.52'));
      else if (data.skipped) setNotice(tx('msg.53'));
      else setNotice(tx('msg.54'));
      await load();
    } catch (e: any) {
      setSendError(e?.message || tx('msg.55'));
      await load(); // 失敗も履歴に残るので再読み込み
    } finally {
      setSending(false);
    }
  };

  const togglePassport = async (id: string, received: boolean) => {
    try {
      const res = await fetch(`/api/admin/reservations/${id}/passport`, {
        method: 'PATCH',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ received }),
      });
      if (!res.ok) throw new Error(await readError(res));
      await load();
    } catch (e: any) {
      setSendError(e?.message || tx('msg.56'));
    }
  };

  // Ctrl+Enter / ⌘+Enter で送信
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); }
  };

  return (
    <div className="messages-page thread-page">
      <div className="thread-header">
        <button type="button" className="btn-back" onClick={() => navigate('/admin/messages')}>{tx('msg.57')}</button>
        <div className="thread-title">
          <h1>{detail?.guest_name || tx('msg.33')}</h1>
          <span className="thread-email">{email}</span>
        </div>
      </div>

      {error && <div className="msg-error" role="alert">⚠ {error}</div>}

      {detail && detail.reservations.length > 0 && (
        <div className="thread-reservations">
          {detail.reservations.map((r) => (
            <button
              type="button"
              key={r.id}
              className="resv-chip"
              onClick={() => navigate(`/admin/reservations?highlight=${r.id}`)}
              title={tx('msg.58')}
            >
              {r.property_name}　{r.checkin_date}〜{r.checkout_date}
              <span className={`status-badge ${r.status.toLowerCase()}`}>{STATUS_LABEL[r.status] || r.status}</span>
              <label className="passport-toggle" onClick={(e) => e.stopPropagation()} title={tx('msg.60')}>
                <input
                  type="checkbox"
                  checked={Number(r.passport_received) === 1}
                  onChange={(e) => togglePassport(r.id, e.target.checked)}
                />
                {tx('msg.61')}
              </label>
            </button>
          ))}
        </div>
      )}

      <div className="message-timeline">
        {loading ? (
          <p className="msg-empty">{tx('msg.26')}</p>
        ) : !detail || detail.messages.length === 0 ? (
          <p className="msg-empty">{tx('msg.62')}</p>
        ) : (
          detail.messages.map((m) => (
            <div key={m.id} className={`message ${m.direction} ${m.kind} ${m.status}`}>
              <div className="message-head">
                <span className="message-who">
                  {m.direction === 'in' ? (detail.guest_name || tx('msg.63')) : m.kind === 'system' ? tx('msg.64') : tx('msg.65')}
                </span>
                <span className="message-time">{fmtTime(m.created_at)}</span>
                <span className={`send-status ${m.status}`}>{SEND_STATUS_LABEL[m.status] || m.status}</span>
                <div className="msg-menu-wrap" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="row-menu-btn"
                    aria-label={tx('msg.42')}
                    aria-haspopup="menu"
                    aria-expanded={msgMenuFor === m.id}
                    onClick={() => setMsgMenuFor(msgMenuFor === m.id ? null : m.id)}
                  >
                    ⋯
                  </button>
                  {msgMenuFor === m.id && (
                    <div className="row-menu" role="menu">
                      <button type="button" role="menuitem" className="row-menu-item danger" onClick={() => removeMessage(m)}>
                        {tx('msg.44')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {m.subject && <div className="message-subject">{m.subject}</div>}
              <div className="message-body">{m.body}</div>
              {m.error && <div className="message-error">{tx('msg.66')} {m.error}</div>}
              {m.direction === 'in' && Number(m.flagged) === 1 && (
                <div className="message-error">{tx('msg.67')}</div>
              )}
              {m.direction === 'in' && m.from_address && m.from_address !== m.guest_email && (
                <div className="message-note">{tx('msg.68')} {m.from_address}</div>
              )}
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* 送信フォーム（画面の一番下） */}
      <div className="composer">
        <div className="composer-tabs">
          <button type="button" className={mode === 'out' ? 'active' : ''} onClick={() => setMode('out')}>{tx('msg.69')}</button>
          <button type="button" className={mode === 'in' ? 'active' : ''} onClick={() => setMode('in')}>{tx('msg.70')}</button>
        </div>
        <div className="composer-row">
          <input
            type="text"
            placeholder={mode === 'out' ? tx('msg.71') : tx('msg.72')}
            value={subject}
            maxLength={200}
            onChange={(e) => setSubject(e.target.value)}
          />
          {detail && detail.reservations.length > 1 && mode === 'out' && (
            <select value={reservationId} onChange={(e) => setReservationId(e.target.value)} title={tx('msg.73')}>
              {detail.reservations.map((r) => (
                <option key={r.id} value={r.id}>{r.property_name} {r.checkin_date}</option>
              ))}
            </select>
          )}
        </div>
        <textarea
          placeholder={mode === 'out' ? tx('msg.74') : tx('msg.75')}
          value={body}
          maxLength={10000}
          rows={5}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
        />
        {sendError && <div className="msg-error">⚠ {sendError}</div>}
        {notice && <div className="msg-notice">{notice}</div>}
        <div className="composer-actions">
          <button type="button" className="btn-send" disabled={sending} onClick={send}>
            {sending ? tx('msg.76') : mode === 'out' ? tx('msg.35') : tx('msg.77')}
          </button>
        </div>
      </div>
    </div>
  );
}

function MessagesPage() {
  const { email } = useParams<{ email?: string }>();
  return email ? <ThreadView email={decodeURIComponent(email).toLowerCase()} /> : <ThreadList />;
}

export default MessagesPage;
